import {Fragment} from "react";
import {colorHex, iconSymbol} from "../public/js/data/avatars.js";
import {Player} from "../public/js/game/player.js";
import {artClass, numberText} from "../public/js/utils/sanitize.js";
import {useAlertExpiry} from "./useGame.js";
import {TurnClock} from "./TurnClock.jsx";
import {GambleReveal} from "./GambleReveal.jsx";
import {MinigamePanel} from "./MinigamePanel.jsx";

// The stacked turn screen: header band (turn, mana pips, chips, log strip), a
// scrolling pile of overlapping card rows that expand in place, and a pinned
// footer. Tapping a row is selection; the expanded card's CTA goes through
// requestPlaySelectedCard, so enemy cards still open the target picker.
// Every answer it paints — cost, affordability, button copy, hints — comes from
// the controller, where the coverage gate can see it.

function HandCard({controller, entry}) {
  const {card, cost, index, blocked, playable, selected, cta, detail, fresh} = entry;

  return (
    <div
      className={`hand-card${selected ? " expanded" : ""}${blocked && !selected ? " blocked" : ""}`}
      style={{zIndex: selected ? 30 : index, "--tilt": `${(index % 2 ? 1 : -1) * 0.9}deg`}}
      role="button"
      tabIndex={0}
      onClick={() => (selected ? controller.cancelSelection() : controller.selectCard(index))}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        if (selected) controller.cancelSelection();
        else controller.selectCard(index);
      }}
    >
      <div className="hand-card-row">
        <span className={`hand-art ${artClass(card.image)}`}></span>
        <div className="hand-card-info">
          <span className="hand-card-name">
            {card.name}
            {fresh && <span className="hand-card-new">Ny</span>}
          </span>
          <span className="hand-card-meta">{card.text} · {card.tag}</span>
        </div>
        <span className={`hand-card-cost${blocked ? " blocked" : ""}`}>{cost}</span>
      </div>
      {selected && (
        <div className="hand-card-body">
          <p className="hand-card-effect">{card.text}</p>
          <div className="hand-card-tags">
            <span className="turn-chip sage">{card.tag}</span>
            <span className="hand-card-costlabel">
              {cost} mana{detail}
            </span>
          </div>
          <div className="hand-card-actions">
            <button
              className="o-btn ghost"
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                controller.cancelSelection();
              }}
            >
              Stäng
            </button>
            <button
              className="o-btn primary cta"
              type="button"
              disabled={!playable}
              onClick={(event) => {
                event.stopPropagation();
                controller.requestPlaySelectedCard();
              }}
            >
              {cta}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function Phone({controller, snapshot}) {
  const {game, playedThisTurn, tableOpen, historyOpen, leaveArmed} = snapshot;
  const me = controller.myPlayer();
  const dead = Player.isDead(me);
  const drinkAlert = controller.drinkAlert();
  useAlertExpiry(drinkAlert?.id ?? null);
  // Which cards the last play took, gave or drew for this player.
  const cardNotice = controller.cardNotice();
  useAlertExpiry(cardNotice?.id ?? null, 12100);
  // A party lobby: seated, nothing to play yet. The match screen stays out of it.
  const waiting = controller.phoneWaiting();
  const figure = controller.turnFigure();
  const deckChip = controller.deckChip();

  const nextName = controller.nextPlayerName();
  const targets = controller.targetSelection();
  const hand = controller.handCards();
  const table = controller.tableSeats();
  const historyRows = historyOpen ? controller.matchHistory() : [];
  // The match is over: this phone's own numbers and the evening's awards.
  const summary = controller.matchSummary();

  return (
    <aside className="phone" aria-label="Din mobilhand">
      <header className="turn-header">
        {/* Secondary actions live up here, as far from the thumb as the screen allows:
            at the bottom, Lämna sat directly under Avsluta tur and was one stray tap
            from ending someone's match — and a started match cannot be rejoined. */}
        <div className="phone-topbar">
          {controller.canLeaveMatch() && (
            <button
              className={leaveArmed ? "topbar-button leave armed" : "topbar-button leave"}
              type="button"
              onClick={() => controller.requestLeave()}
            >
              {controller.leaveLabel()}
            </button>
          )}
          {!waiting && (
            <button
              className="topbar-button history"
              type="button"
              aria-expanded={historyOpen}
              onClick={() => controller.toggleHistory()}
            >
              <span aria-hidden="true">🕘</span> Historik
            </button>
          )}
        </div>
        <div className="turn-title-row">
          {/* The figure of whoever the headline names — your own between matches. */}
          {figure && (
            <div className="turn-avatar" style={{"--seat-color": figure.color}}>
              <span aria-hidden="true">{figure.icon}</span>
            </div>
          )}
          <div className="turn-title">
            <span className="turn-kicker">{controller.turnKicker()}</span>
            <span className="turn-name">{controller.turnHeadline()}</span>
          </div>
          {!waiting && (
            <div className="turn-mana">
              {me.maxMana > 0 && (
                <div className="mana-pips" aria-hidden="true">
                  {Array.from({length: me.maxMana}, (_, pip) => (
                    <span key={pip} className={`mana-pip${pip < me.mana ? "" : " spent"}`}></span>
                  ))}
                </div>
              )}
              <span className="mana-count">{numberText(me.mana)}/{numberText(me.maxMana)} mana</span>
            </div>
          )}
        </div>
        {!waiting && (
          <div className="turn-chips">
            <span className="turn-chip outline">{numberText(me.sipsTaken)}/{numberText(me.maxSips)} klunkar</span>
            {/* Once the deck is empty this turns into the warning of what the next turn
                start costs — fatigue used to arrive with no notice and no explanation. */}
            <span className={deckChip.warning ? "turn-chip warning" : "turn-chip"}>{deckChip.label}</span>
            {nextName && <span className="turn-chip">{nextName} är näst</span>}
            <TurnClock controller={controller} />
          </div>
        )}
        <div className="turn-log">{game.log || "Välj ett kort och en motståndare."}</div>
      </header>

      {historyOpen && (
        <section
          className="history-modal"
          onClick={(event) => {
            if (event.target === event.currentTarget) controller.toggleHistory();
          }}
        >
          <div className="history-dialog">
            <div className="history-dialog-header">
              <p className="eyebrow">Spelhistorik</p>
              <button className="o-btn ghost" type="button" onClick={() => controller.toggleHistory()}>
                Stäng
              </button>
            </div>
            <div className="history-dialog-list">
              {historyRows.length === 0 ? (
                <p className="history-empty">Inga kort har spelats än.</p>
              ) : (
                historyRows.map((row) => (
                  <article key={row.id} className="history-entry">
                    <strong>{row.title}</strong>
                    {row.detail && <p>{row.detail}</p>}
                    {row.target && <span className="event-target">{row.target}</span>}
                    {row.drinks.length ? (
                      <ul>{row.drinks.map((line, index) => <li key={index}>{line}</li>)}</ul>
                    ) : (
                      <small>Ingen behövde dricka.</small>
                    )}
                  </article>
                ))
              )}
            </div>
          </div>
        </section>
      )}

      {/* With no TV, the phone is the board as well. Your side first, then theirs, with
          each seat's klunkar and minions — everything the room would otherwise be
          reading off the big screen. Empty in party mode, where the TV does this. */}
      {table.length > 0 && (
        <section className={tableOpen ? "table-strip" : "table-strip folded"} aria-label="Bordet">
          {/* The strip and the hand share one small screen, so the strip folds away.
              Folded it keeps one chip per seat: you still need to see who is standing
              and how far gone they are, just not at full size. */}
          <button
            className="table-toggle"
            type="button"
            aria-expanded={tableOpen}
            onClick={() => controller.toggleTable()}
          >
            <span className="table-toggle-label">Bordet</span>
            {!tableOpen && (
              <span className="table-chips">
                {table.map((seat) => (
                  <span
                    key={seat.id}
                    className={`table-chip${seat.ally ? " ally" : ""}${seat.out ? " out" : ""}${seat.active ? " active" : ""}`}
                    style={{"--seat-color": seat.color}}
                  >
                    <span aria-hidden="true">{seat.icon}</span>
                    <b>{seat.chip}</b>
                  </span>
                ))}
              </span>
            )}
            <span className="table-toggle-action">{tableOpen ? "Dölj" : "Visa"}</span>
          </button>
          {tableOpen && table.map((seat) => (
            <Fragment key={seat.id}>
            {seat.heading && <p className="table-heading">{seat.heading}</p>}
            <article
              className={[
                "table-seat",
                seat.ally ? "ally" : "foe",
                seat.mine ? "me" : "",
                seat.active ? "active" : "",
                seat.out ? "out" : "",
              ].join(" ")}
              style={{"--seat-color": seat.color, "--hp": `${seat.danger}%`}}
            >
              <span className="avatar"><span aria-hidden="true">{seat.icon}</span></span>
              <div className="table-seat-info">
                <span className="table-seat-name">{seat.name}{seat.mine ? " (du)" : ""}</span>
                <span className="table-seat-sips">{seat.sips} klunkar</span>
                <div className="hp-track"><div className="hp-fill"></div></div>
              </div>
              {seat.minions.length > 0 && (
                <div className="table-seat-minions">
                  {seat.minions.map((minion) => (
                    <span key={minion.id} className="table-minion" title={minion.name}>
                      {minion.name} <b>{minion.hp}</b>
                    </span>
                  ))}
                </div>
              )}
            </article>
            </Fragment>
          ))}
        </section>
      )}

      {waiting && (
        <section className="phone-waiting" aria-label="Väntar på start">
          <h2>Du är med!</h2>
          <p>{waiting.status}</p>
          <span className="turn-chip">{waiting.headcount}</span>
          <ul className="phone-waiting-roster">
            {game.players.map((player) => (
              <li
                key={player.id}
                className={player.id === me.id ? "table-chip me" : "table-chip"}
                style={{"--seat-color": colorHex(player.color)}}
              >
                <span aria-hidden="true">{iconSymbol(player.icon)}</span>
                {player.name}
              </li>
            ))}
          </ul>
        </section>
      )}

      {!waiting && (
        <div className="hand">
          <div className="hand-label">
            <span className="hand-title">Din hand</span>
            <span className="hand-hint">{hand.length} kort · tryck för att läsa</span>
          </div>
          {hand.map((entry) => (
            <HandCard key={`${entry.card.id}-${entry.index}`} controller={controller} entry={entry} />
          ))}
          {hand.length === 0 && (
            <div className="hand-empty">Handen är tom. Avsluta turen för att dra nya kort.</div>
          )}
          {playedThisTurn.length > 0 && (
            <div className="played-section">
              <span className="hand-title">Spelat den här turen</span>
              <div className="turn-chips">
                {playedThisTurn.map((name, index) => (
                  <span key={`${name}-${index}`} className="turn-chip accent">{name}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!waiting && (
        <footer className="turn-footer">
          <button
            className="o-btn primary end-turn"
            type="button"
            disabled={!controller.canEndTurn()}
            onClick={() => controller.endTurn()}
          >
            Avsluta tur
          </button>
          <div className="turn-foot-hint">{controller.turnFootHint()}</div>
        </footer>
      )}

      {targets && (
        <section
          className="target-modal"
          onClick={(event) => {
            if (event.target === event.currentTarget) controller.closeTargetModal();
          }}
        >
          <div className="target-dialog">
            <div className="target-dialog-header">
              <div>
                <p className="eyebrow">Välj mål</p>
                {/* The eyebrow above already says "Välj mål", so the heading is just
                    the card. The long version wrapped to four lines on a 320px phone
                    and pushed the targets themselves off the first screenful. */}
                <h3>{targets.card.name}</h3>
              </div>
              <button className="plain-button" type="button" onClick={() => controller.closeTargetModal()}>
                Avbryt
              </button>
            </div>
            <div className="target-list">
              <div className="target-buttons">
                {targets.enemies.map((enemy) => (
                  <button
                    key={enemy.id}
                    className="plain-button target-button"
                    type="button"
                    onClick={() => controller.playSelectedCard(game.players.indexOf(enemy))}
                  >
                    {/* The figure, so a table of ten is picked by face as well as by name. */}
                    <span className="avatar" style={{"--seat-color": colorHex(enemy.color)}}>
                      <span aria-hidden="true">{iconSymbol(enemy.icon)}</span>
                    </span>
                    <strong>{enemy.name}</strong>
                    <span className="target-detail">
                      {numberText(enemy.sipsTaken)}/{numberText(enemy.maxSips)} klunkar ·{" "}
                      {enemy.minions.length} försvarare
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Not a cover like the drink alert: the player wants to see the hand it is
          talking about, so it sits over the header and the new cards are marked "Ny". */}
      {cardNotice && !dead && (
        <button className="card-notice" type="button" onClick={() => controller.dismissCardNotice()}>
          <span className="card-notice-title">{cardNotice.title}</span>
          {cardNotice.lines.map((line, index) => (
            <span key={index} className="card-notice-line">{line}</span>
          ))}
          <span className="card-notice-hint">Tryck för att stänga</span>
        </button>
      )}

      {/* Blackjack or the ballot: the whole phone while it runs, since nothing else can
          be played until it is done. The drink alert lands over its result. */}
      <MinigamePanel controller={controller} />

      {/* Covers the whole phone, so the whole of it dismisses: a hit right before
          your turn otherwise held the hand for the full five seconds. */}
      {drinkAlert && (
        <section className={drinkAlert.next ? "drink-alert fatigue" : "drink-alert"} onClick={() => controller.dismissDrinkAlert()}>
          <div>
            <p className="eyebrow">{drinkAlert.title}</p>
            <h2>Drick {drinkAlert.amount}</h2>
            <p>{drinkAlert.source}</p>
            {drinkAlert.next && <p className="drink-alert-next">{drinkAlert.next}</p>}
            <button className="o-btn primary" type="button">Skål!</button>
          </div>
        </section>
      )}

      {dead && !summary && (
        <section className="death-screen">
          <div>
            <p className="eyebrow">Ute ur matchen</p>
            <h2>DU är död</h2>
            <p>Du har nått {numberText(me.maxSips)} klunkar. Ta en shot!</p>
            <button className="o-btn primary" type="button" onClick={() => controller.leaveGame()}>
              Lämna matchen
            </button>
          </div>
        </section>
      )}

      {summary && (
        <section className={summary.won ? "death-screen match-summary winner" : "death-screen match-summary"}>
          <div>
            {summary.won && <span className="winner-chicken" aria-hidden="true"></span>}
            <p className="eyebrow">Matchen är slut</p>
            <h2>{summary.headline}</h2>
            <p>{summary.detail}</p>
            <h3 className="summary-heading">Din statistik</h3>
            <dl className="summary-stats">
              {summary.rows.map((row) => (
                <div key={row.key} className="summary-stat">
                  <dt>{row.label}</dt>
                  <dd>{row.value}</dd>
                </div>
              ))}
            </dl>
            {summary.awards.length > 0 && (
              <>
                <h3 className="summary-heading">Kvällens priser</h3>
                <ul className="summary-awards">
                  {summary.awards.map((award) => (
                    <li key={award.id} className={award.mine ? "summary-award mine" : "summary-award"}>
                      <span className="summary-award-icon" aria-hidden="true">{award.icon}</span>
                      <span className="summary-award-title">{award.title}</span>
                      <span className="summary-award-who">{award.who} · {award.detail}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            <button className="o-btn primary" type="button" onClick={() => controller.leaveGame()}>
              Lämna matchen
            </button>
          </div>
        </section>
      )}
      {/* Over everything, the death screen and the end screen included: a coin that
          knocks you out must still be seen landing before the screen says so. */}
      <GambleReveal controller={controller} />
    </aside>
  );
}
